import { useEffect } from 'react'
import { Link } from 'react-router-dom'

/**
 * Public Terms & Conditions page rendered as HTML so Google's branding/OAuth
 * verifier can crawl it. The text content mirrors OVACS_TERMS_AND_CONDITIONS.pdf —
 * keep both in sync when one changes.
 *
 * URL: https://ovacs.com/terms
 */
const TermsPage = () => {
  useEffect(() => {
    document.title = 'Terms & Conditions — OVACS'
    const meta = document.querySelector('meta[name="description"]') || (() => {
      const m = document.createElement('meta')
      m.name = 'description'
      document.head.appendChild(m)
      return m
    })()
    meta.content =
      'OVACS Terms & Conditions — the rules governing your use of the OVACS document archiving and tracking platform.'
    return () => { document.title = 'OVACS' }
  }, [])

  return (
    <div className="min-h-screen bg-white">
      {/* Top bar */}
      <header className="border-b border-gray-200 bg-white">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 font-semibold text-gray-900">
            <span className="text-cyan-600">OVACS</span>
          </Link>
          <nav className="text-sm text-gray-600 flex items-center gap-5">
            <Link to="/landing" className="hover:text-cyan-700">Home</Link>
            <Link to="/privacy" className="hover:text-cyan-700">Privacy</Link>
            <Link to="/contact" className="hover:text-cyan-700">Contact</Link>
          </nav>
        </div>
      </header>

      {/* Body */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">Terms &amp; Conditions</h1>
        <p className="text-gray-500 italic mb-10">OVACS — Document Archiving and Tracking System</p>

        {/* Summary callout */}
        <div className="bg-cyan-50 border-l-4 border-cyan-500 p-5 rounded mb-10 text-sm text-gray-800 leading-relaxed">
          <p>
            These Terms govern your use of the OVACS website and application. By creating an account or otherwise using OVACS, you agree to the points below. If you do not agree, please do not use the service.
          </p>
        </div>

        <Section title="Terms and Conditions">
          <ol className="list-decimal list-outside ml-5 space-y-3">
            <li>
              <strong>OVACS is a website and application for document archiving and tracking.</strong>
            </li>
            <li>
              <strong>Document access is only for subscribers of the account holder.</strong>
            </li>
            <li>
              <strong>The account holder has the right to add subscribers to their account and determine their subscription level.</strong>
            </li>
            <li>
              <strong>The account holder determines the security level of the document and who has the right to view it</strong> from the subscribers in their account.
            </li>
            <li>
              <strong>The application management reserves the right to modify subscription prices at any time.</strong>
            </li>
            <li>
              <strong>OVACS is interested in receiving subscriber feedback and opinions</strong>, and is committed to correcting all errors resulting from programming defects, and studies all incoming suggestions and works to implement them if they are in favor of making the website and application work in an easier and better way.
            </li>
          </ol>
        </Section>

        <hr className="my-10 border-gray-200" />

        <p className="text-center text-gray-600 italic">
          By using OVACS, you agree to these terms and conditions.
        </p>

        <hr className="my-10 border-gray-200" />

        <Section title="Privacy">
          <p>
            For details about how we collect, use, share, and protect your personal data, please see our{' '}
            <Link to="/privacy" className="text-cyan-700 underline">Privacy Policy</Link>.
          </p>
        </Section>

        <Section title="Contact">
          <p>
            For questions about these Terms, email us at{' '}
            <a className="text-cyan-700 underline" href="mailto:info@ovacs.com">info@ovacs.com</a>.
          </p>
        </Section>

        <hr className="my-12 border-gray-200" />
        <p className="text-xs text-gray-500">
          A PDF version of these terms is also available at{' '}
          <a className="text-cyan-700 underline" href="/OVACS_TERMS_AND_CONDITIONS.pdf" target="_blank" rel="noopener noreferrer">
            /OVACS_TERMS_AND_CONDITIONS.pdf
          </a>.
        </p>
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-200 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 py-6 text-sm text-gray-500 flex flex-wrap items-center justify-between gap-3">
          <div>© {new Date().getFullYear()} OVACS. All rights reserved.</div>
          <nav className="flex items-center gap-4">
            <Link to="/landing" className="hover:text-cyan-700">Home</Link>
            <Link to="/privacy" className="hover:text-cyan-700">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-cyan-700">Terms</Link>
            <Link to="/contact" className="hover:text-cyan-700">Contact</Link>
          </nav>
        </div>
      </footer>
    </div>
  )
}

const Section = ({ title, children }) => (
  <section className="mb-10">
    <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-3 scroll-mt-20" id={slugify(title)}>{title}</h2>
    <div className="space-y-3 text-gray-700 leading-relaxed text-[15px]">
      {children}
    </div>
  </section>
)

function slugify(s) {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
}

export default TermsPage
